<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { isAxiosError } from 'axios';
import { useTranslation } from '@/utils/locales/messages';
import { storeToRefs } from 'pinia';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useDialog } from '@/shared/composables/useDialog';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { contactsApi } from '../api/contactsApi';
import type {
    CompanyRelation,
    CompanyRelationRoleAddress,
    CompanyRelationPositionDetail,
} from '../types/contact.types';

interface Props {
    contactId: string;
    readonly?: boolean;
    /**
     * CONTACT_JSON_DATA 원본 (service_address / business_address 등)
     */
    contactJsonData?: string | Record<string, any> | null;
    /**
     * L_GLOBAL_CONTACT_PROFILE.JSON_DATA 원본 — 주거지(residential_address)가 여기만 있을 때 사용
     */
    profileJsonData?: string | Record<string, unknown> | null;
    /**
     * 폼에서 내려주는 residential_address (배열/객체). JSON_DATA와 중복될 수 있음
     */
    residentialAddress?: unknown;
    /** 엑셀 파일명용 — 컨택트 이름 */
    contactName?: string;
    /** 엑셀 파일명용 — 컨택트 등록번호(reg_no) */
    contactRegNo?: string;
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false,
    contactJsonData: null,
    profileJsonData: null,
    residentialAddress: undefined,
    contactName: '',
    contactRegNo: ''
});

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

const companyRelations = ref<CompanyRelation[]>([]);
const isLoading = ref(false);
const PAGE_SIZE = 50;
const loadingMore = ref(false);
const hasMore = ref(true);
const totalCount = ref<number | null>(null);
const searchDraft = ref('');
const scrollRootRef = ref<HTMLElement | null>(null);
const loadMoreSentinelRef = ref<HTMLElement | null>(null);
let loadMoreObserver: IntersectionObserver | null = null;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const countryCodeNameMap = ref<Map<string, string>>(new Map());

const normalizeKey = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const isLikelyCodeValue = (value: string): boolean => /^[A-Z]\d{5,}$/.test(value);

const resolveCountryDisplay = (rawCountry: unknown): string => {
    const value = String(rawCountry || '').trim();
    if (!value) return '';
    const key = normalizeKey(value);
    return countryCodeNameMap.value.get(key) || value;
};

function collapseAddressCommas(line: string): string {
    return String(line || '')
        .replace(/,\s*,+/g, ', ')
        .replace(/^,\s*|,\s*$/g, '')
        .trim();
}

const normalizeAddressText = (address: any): string => {
    if (!address || typeof address !== 'object') return '';
    const countryDisplay = resolveCountryDisplay(address.country);
    const joined = [
        address.street01,
        address.street02,
        address.street03,
        address.post_code || address.postcode || address.postalCode,
        address.city,
        address.state,
        countryDisplay
    ]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(', ');
    // 주소는 country 포함 전부 대문자로 표시
    return collapseAddressCommas(joined).toUpperCase();
};

const loadCountryCodeNameMap = async () => {
    try {
        const response = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
        const codes = (response as any).codes || response.data?.codes || [];
        const map = new Map<string, string>();
        for (const code of codes) {
            const totalCode = normalizeKey(code?.totalCode || code?.TOTAL_CODE);
            const codeName = String(
                code?.defaultCodeName ||
                code?.DEFAULT_CODE_NAME ||
                code?.codeName ||
                code?.CODE_NAME ||
                ''
            ).trim();
            if (!totalCode || !codeName) continue;
            if (totalCode.endsWith('000')) continue;
            if (!map.has(totalCode)) {
                map.set(totalCode, codeName);
            }
        }
        countryCodeNameMap.value = map;
    } catch (error) {
        console.error('[CompanyRelationsTable] Failed to load country code names:', error);
    }
};

const parsedContactJson = computed<Record<string, any>>(() => {
    const raw = props.contactJsonData;
    if (typeof raw === 'string') {
        try {
            const o = JSON.parse(raw);
            return o && typeof o === 'object' ? o : {};
        } catch {
            return {};
        }
    }
    if (raw && typeof raw === 'object') return raw as Record<string, any>;
    return {};
});

const parsedProfileJson = computed<Record<string, any>>(() => {
    const raw = props.profileJsonData;
    if (typeof raw === 'string') {
        try {
            const o = JSON.parse(raw);
            return o && typeof o === 'object' ? o : {};
        } catch {
            return {};
        }
    }
    if (raw && typeof raw === 'object') return raw as Record<string, any>;
    return {};
});

function isPrimaryAddressDisplayFlag(value: unknown): boolean {
    return value === 1 || value === '1' || value === true;
}

function rowHasPrimaryOrSync(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    const ip = row.isPrimaryAddress ?? row.is_primary_address;
    const syn = row.IsSync ?? row.isSync ?? row.IS_SYNC;
    return isPrimaryAddressDisplayFlag(ip) || isPrimaryAddressDisplayFlag(syn);
}

function residentialRowsFromProp(addr: unknown): any[] {
    if (addr == null) return [];
    if (Array.isArray(addr)) return addr.filter((x) => x && typeof x === 'object');
    if (typeof addr === 'object') return [addr];
    return [];
}

/**
 * 주거지 표시용: isPrimaryAddress·IsSync 없는 행 우선(CONTACT_JSON 단일 객체 포함),
 * 없으면 내용 있는 첫 행, 그다음 부모에서 넘긴 residential.
 */
function pickResidentialRowForLine(addr: unknown): any | null {
    const candidates = residentialRowsFromProp(addr);
    if (candidates.length === 0) return null;

    const hasLine = (r: any) => !!normalizeAddressText(r).trim();

    const noFlag = candidates.find((r) => !rowHasPrimaryOrSync(r) && hasLine(r));
    if (noFlag) return noFlag;

    const anyLine = candidates.find((r) => hasLine(r));
    if (anyLine) return anyLine;

    return candidates[0] ?? null;
}

const defaultResidentialLine = computed(() => {
    // 주거지는 entity_cd와 무관. 소스: CONTACT_JSON_DATA → 프로필 JSON_DATA → 폼 residential
    let row = pickResidentialRowForLine(parsedContactJson.value?.residential_address);
    if (!row || !normalizeAddressText(row).trim()) {
        row = pickResidentialRowForLine(parsedProfileJson.value?.residential_address);
    }
    if (!row || !normalizeAddressText(row).trim()) {
        row = pickResidentialRowForLine(props.residentialAddress);
    }
    return normalizeAddressText(row).trim();
});

/** entity_cd·position 둘 다 없으면 동기 placeholder 등으로 간주해 More/모달에서 제외 */
function jsonServiceBusinessRowQualifies(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    const ent = String(row.entity_cd || row.ENTITY_CD || row.entityCd || '').trim();
    const pos = String(row.position || row.POSITION || '').trim();
    return !!(ent || pos);
}

const qualifyingServiceRows = computed(() => {
    const list = Array.isArray(parsedContactJson.value?.service_address)
        ? parsedContactJson.value.service_address
        : [];
    return list.filter(jsonServiceBusinessRowQualifies);
});

const qualifyingBusinessRows = computed(() => {
    const list = Array.isArray(parsedContactJson.value?.business_address)
        ? parsedContactJson.value.business_address
        : [];
    return list.filter(jsonServiceBusinessRowQualifies);
});

function myRoleAddressLines(relation: CompanyRelation | null | undefined): CompanyRelationRoleAddress[] {
    if (!relation || !Array.isArray(relation.roleAddresses)) return [];
    return relation.roleAddresses.filter((r) => String(r.addressLine || '').trim() !== '');
}

/** 주소 표시용: country 포함 전부 대문자 */
function upperAddressLine(value: unknown): string {
    return String(value || '').toUpperCase();
}

/** MY: API roleAddresses / 그 외: 레거시 JSON·주거지 */
function relationPrimaryAddressLine(relation: CompanyRelation): string {
    let line = '';
    if (isMyJurisdiction.value) {
        const summary = String(relation.addressSummary || '').trim();
        line = summary || String(myRoleAddressLines(relation)[0]?.addressLine || '').trim();
    } else {
        line = defaultResidentialLine.value || String(relation.address || '').trim();
    }
    return upperAddressLine(line);
}

/** 주소 라벨을 표준 유형 키로 정규화 (컬럼 그룹핑용) */
function addressTypeKey(label: string): string {
    const n = String(label || '').trim().toLowerCase();
    if (!n) return 'address';
    if (n.includes('residential')) return 'residential';
    if (n.includes('service')) return 'service';
    if (n.includes('business')) return 'business';
    if (n.includes('registered')) return 'registered';
    if (n.includes('correspondence')) return 'correspondence';
    return n;
}

/** 주소 유형별 엑셀 컬럼 헤더(다국어) */
function addressColumnHeader(typeKey: string, sampleLabel: string): string {
    switch (typeKey) {
        case 'residential':
            return t('contact.Residential Address') || 'Residential Address';
        case 'service':
            return t('contact.Service Address') || 'Service Address';
        case 'business':
            return t('contact.Business Address') || 'Business Address';
        case 'registered':
            return t('contact.Registered Address') || 'Registered Address';
        case 'address':
            return t('contact.Address') || 'Address';
        default: {
            const lbl = String(sampleLabel || typeKey).trim();
            return /address/i.test(lbl) ? lbl : `${lbl} ${t('contact.Address') || 'Address'}`;
        }
    }
}

/**
 * 엑셀 다운로드용: 해당 relation의 주소 전체(유형 + 라인)를 순서대로 반환.
 * MY는 roleAddresses(모달과 동일) 전부, 없으면 요약/레거시 한 줄로 폴백.
 */
function relationAddressEntries(relation: CompanyRelation): { typeKey: string; label: string; line: string }[] {
    const roleLines = myRoleAddressLines(relation);
    if (roleLines.length > 0) {
        return roleLines.map((r) => {
            const label = String(r.addressLabel || '').trim();
            return { typeKey: addressTypeKey(label), label, line: upperAddressLine(r.addressLine) };
        });
    }
    const single = relationPrimaryAddressLine(relation);
    return single ? [{ typeKey: 'address', label: '', line: single }] : [];
}

function relationMoreAddressCount(relation: CompanyRelation): number {
    if (isMyJurisdiction.value) {
        const lines = myRoleAddressLines(relation);
        return lines.length > 1 ? lines.length - 1 : 0;
    }
    return getExtraAddressBundle(relation).count;
}

const addressDetailModalOpen = ref(false);
/** More/모달에 넣을 현재 테이블 행 */
const addressModalRelation = ref<CompanyRelation | null>(null);

const modalMyRoleAddresses = computed(() =>
    addressModalRelation.value ? myRoleAddressLines(addressModalRelation.value) : []
);

const modalUsesMyRoleAddresses = computed(
    () => isMyJurisdiction.value && modalMyRoleAddresses.value.length > 0
);

function normPosToken(s: string): string {
    return String(s || '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '')
        .replace(/_/g, '');
}

function relationEntityKey(relation: any): string {
    return normalizeKey(relation?.companyCd || relation?.company_cd || relation?.entity_cd || relation?.ENTITY_CD);
}

function jsonEntityKey(row: any): string {
    return normalizeKey(row?.entity_cd || row?.ENTITY_CD || row?.entityCd);
}

function entityCdMatchesRelation(jsonRow: any, relation: any): boolean {
    const je = jsonEntityKey(jsonRow);
    const re = relationEntityKey(relation);
    if (!je || !re) return false;
    return je === re;
}

function officerRelationPositionTokens(relation: any): string[] {
    const raw = String(relation?.position || '').trim();
    if (!raw) return [];
    return raw
        .split(/[,;]+/)
        .map((p) => normPosToken(p))
        .filter(Boolean);
}

/** Officer 행: 같은 entity + JSON position이 해당 Officer 직책(예: Director)과 일치하는 service_address만 */
function jsonServiceRowMatchesOfficerRelation(jsonRow: any, relation: any): boolean {
    if (!jsonServiceBusinessRowQualifies(jsonRow)) return false;
    if (!entityCdMatchesRelation(jsonRow, relation)) return false;
    const tokens = officerRelationPositionTokens(relation);
    if (tokens.length === 0) return false;
    const jp = normPosToken(String(jsonRow?.position || jsonRow?.POSITION || ''));
    if (!jp) return false;
    return tokens.some((t) => jp === t || jp.includes(t) || t.includes(jp));
}

/** Beneficial Owner 행: 같은 entity + business JSON의 position이 BO 계열 */
function jsonBusinessRowMatchesBeneficialRelation(jsonRow: any, relation: any): boolean {
    if (!jsonServiceBusinessRowQualifies(jsonRow)) return false;
    if (!entityCdMatchesRelation(jsonRow, relation)) return false;
    const raw = String(jsonRow?.position || jsonRow?.POSITION || '').toUpperCase();
    return raw.includes('BENEFICIAL');
}

/**
 * service_address JSON의 position이 주주(L_SHAREHOLDER_DETAIL) 동기화 출처인지.
 * - fullSync 등: SHAREHOLDER (ADDRESS_POSITION_SHAREHOLDER)
 * - 기타/마이그레이션: Shares, Share
 */
function jsonServiceRowPositionIsFromShareholderDetail(jsonRow: any): boolean {
    const jp = normPosToken(String(jsonRow?.position || jsonRow?.POSITION || ''));
    if (!jp) return false;
    if (jp === 'SHARES' || jp === 'SHARE') return true;
    if (jp === 'SHAREHOLDER' || jp.startsWith('SHAREHOLDER')) return true;
    return false;
}

/** Company Relations의 Shares 행(API=L_SHAREHOLDER_DETAIL) ↔ 동일 entity의 주주용 service_address만 */
function jsonServiceRowMatchesSharesRelation(jsonRow: any, relation: any): boolean {
    if (String(relation?.role || '').trim() !== 'Shares') return false;
    if (!jsonServiceBusinessRowQualifies(jsonRow)) return false;
    if (!entityCdMatchesRelation(jsonRow, relation)) return false;
    return jsonServiceRowPositionIsFromShareholderDetail(jsonRow);
}

/**
 * 해당 Company Relation 행에 맞는 service/business JSON 주소만 (More 숫자·모달용)
 */
function getExtraAddressBundle(relation: any): { service: any[]; business: any[]; count: number } {
    const role = String(relation?.role || '').trim();
    let service: any[] = [];
    let business: any[] = [];
    if (role === 'Officer' && relation?.position) {
        service = qualifyingServiceRows.value.filter((r) => jsonServiceRowMatchesOfficerRelation(r, relation));
    } else if (role === 'Shares') {
        service = qualifyingServiceRows.value.filter((r) => jsonServiceRowMatchesSharesRelation(r, relation));
    } else if (role === 'Beneficial Owner') {
        business = qualifyingBusinessRows.value.filter((r) => jsonBusinessRowMatchesBeneficialRelation(r, relation));
    }
    return { service, business, count: service.length + business.length };
}

function openAddressDetailModal(relation: any) {
    addressModalRelation.value = relation;
    addressDetailModalOpen.value = true;
}

const entityCompanyNameMap = computed(() => {
    const m = new Map<string, string>();
    for (const r of companyRelations.value) {
        const anyR = r as any;
        const cd = normalizeKey(anyR?.companyCd || anyR?.company_cd || anyR?.ENTITY_CD || anyR?.entity_cd);
        const nm = String(r.companyName || '').trim();
        if (cd && nm) m.set(cd, nm);
    }
    return m;
});

function entityDisplayName(row: any): string {
    const cd = normalizeKey(row?.entity_cd || row?.ENTITY_CD || row?.entityCd);
    if (!cd) return '';
    return entityCompanyNameMap.value.get(cd) || String(row?.entity_cd || row?.entityCd || '').trim();
}

function positionLabel(row: any): string {
    const raw = String(row?.position || row?.POSITION || '').trim();
    if (!raw) return '';
    return raw
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** 테이블 행: API 그대로(Address 컬럼은 주거지 + More로 별도 표시) */
const tableRelationItems = computed(() => companyRelations.value);

const anyRowHasMoreJsonAddresses = computed(() =>
    tableRelationItems.value.some((r) => getExtraAddressBundle(r).count > 0)
);

const modalFilteredService = computed(() =>
    addressModalRelation.value ? getExtraAddressBundle(addressModalRelation.value).service : []
);
const modalFilteredBusiness = computed(() =>
    addressModalRelation.value ? getExtraAddressBundle(addressModalRelation.value).business : []
);

watch(addressDetailModalOpen, (open) => {
    if (!open) addressModalRelation.value = null;
});

/** Contact 상세와 동일: 세션의 customer.jurisdiction (authStore만 보면 비어 있어 Address 컬럼이 안 나올 수 있음) */
const sessionStore = useSessionStore();
const { user: sessionUser } = storeToRefs(sessionStore);

const isMyJurisdiction = computed(() => {
    const j = String(sessionUser.value?.customer?.jurisdiction || '').toUpperCase();
    return j === 'MY' || j === 'MALAYSIA';
});

const isSgJurisdiction = computed(() => {
    const j = String(sessionUser.value?.customer?.jurisdiction || '').toUpperCase();
    return j === 'SG' || j === 'SINGAPORE';
});

/** MY는 Register 직책별 주소(API). 그 외 레거시 JSON·API address */
const showAddressColumn = computed(() => {
    if (isMyJurisdiction.value) return true;
    if (defaultResidentialLine.value) return true;
    if (anyRowHasMoreJsonAddresses.value) return true;
    return companyRelations.value.some((r) => {
        if (myRoleAddressLines(r).length > 0) return true;
        return r.address != null && String(r.address).trim() !== '';
    });
});

onMounted(() => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });
});

// 다국어 처리된 헤더 (Address 컬럼은 한 줄 표시를 위해 넉넉한 minWidth)
const headers = computed(() => {
    const base: { title: string; key: string; minWidth?: string; width?: string }[] = [
        { title: t('contact.Company Name') || 'Company Name', key: 'companyName', minWidth: '200px' }
    ];
    if (showAddressColumn.value) {
        base.push({
            title: t('contact.Address') || 'Address',
            key: 'address',
            minWidth: '560px',
            width: '50%'
        });
    }
    base.push(
        { title: t('contact.Role') || 'Role', key: 'role', minWidth: '120px' },
        { title: t('contact.Shares') || 'Shares', key: 'shares', minWidth: '100px' }
    );
    return base;
});

// 섹션 제목
const sectionTitle = computed(() => t('contact.Company Relations') || 'Company Relations');

// ─── Company Relations 엑셀 다운로드 ──────────────────────────────
const { confirm, error: dialogError } = useDialog();
const isExporting = ref(false);

/** 테이블 Role 컬럼과 동일한 표시 규칙 */
function relationRoleLabel(item: CompanyRelation): string {
    return item?.role === 'Officer' && item?.position ? item.position : (item?.role || '');
}

/** 테이블 Shares 컬럼과 동일한 표시 규칙 */
function relationSharesText(item: CompanyRelation): string {
    if (item?.role === 'Shares' && item?.shares !== undefined && item?.shares !== null) {
        return `${item.currency ? item.currency + ' ' : ''}${Number(item.shares).toLocaleString()}`;
    }
    return '-';
}

// ─── SG 전용: 직책 선택 다운로드 ──────────────────────────────────
//   MY 는 기존 그대로(확인 → 전량 다운로드). 아래 로직은 SG 에서만 탄다.

const NOMINEE_ROLE = 'Nominee Director';
/** 현행만 내린다. 해임 포함이 필요해지면 이 상수만 false 로. */
const SG_CURRENT_ONLY = true;

/** 살아있는(현행) 직책만. Officer 가 아닌 행은 빈 배열. */
function livePositions(r: CompanyRelation): CompanyRelationPositionDetail[] {
    const detail = r.positionsDetail;
    if (!Array.isArray(detail)) return [];
    return detail.filter((d) => {
        if (SG_CURRENT_ONLY && String(d.cessationDate || '').trim()) return false;
        return !!String(d.name || '').trim();
    });
}

/**
 * 한 행이 걸리는 직책 목록.
 * nominee 행은 Director 이면서 동시에 Nominee Director 다 → 두 값 모두 넣는다.
 * (데이터가 Director 행 하나에 IS_NOMINEE 가 붙는 구조라 빼면 Director 목록에서 사라진다)
 */
function rolesOfRow(r: CompanyRelation): string[] {
    const live = livePositions(r);
    if (!live.length) {
        // Shares / Controller(SG) / Beneficial Owner(MY) 등 — 기존 라벨 그대로
        const lb = relationRoleLabel(r).trim();
        return lb ? [lb] : [];
    }
    const out = new Set<string>();
    for (const d of live) {
        out.add(String(d.name).trim());
        if (d.isNominee) out.add(NOMINEE_ROLE);
    }
    return Array.from(out);
}

/** 실제로 존재하는 직책만. 건수는 표시하지 않는다. */
function buildRoleOptions(rows: CompanyRelation[]): string[] {
    const set = new Set<string>();
    for (const r of rows) for (const lb of rolesOfRow(r)) set.add(lb);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * 고른 직책만 남기고, Officer 행의 position 도 선택분으로 좁힌다.
 * 한 행이 두 직책에 걸려도 필터가 행 단위라 중복 행이 생기지 않는다.
 */
function projectRowsForExcel(rows: CompanyRelation[], roles: string[]): CompanyRelation[] {
    if (!roles.length) return [];
    const want = new Set(roles);
    const wantNominee = want.has(NOMINEE_ROLE);
    const out: CompanyRelation[] = [];

    for (const r of rows) {
        const live = livePositions(r);

        if (!live.length) {
            if (want.has(relationRoleLabel(r).trim())) out.push(r);
            continue;
        }

        const kept = live.filter(
            (d) => want.has(String(d.name).trim()) || (d.isNominee && wantNominee),
        );
        if (!kept.length) continue;

        // 셀에는 실제 POSITION_NAME 을 쓴다. nominee 는 엔티티 Officer 탭의 배지에 대응해 괄호로.
        const names = Array.from(
            new Set(
                kept.map((d) =>
                    d.isNominee ? `${String(d.name).trim()} (Nominee)` : String(d.name).trim(),
                ),
            ),
        ).sort();

        out.push({ ...r, position: names.join(', ') });
    }
    return out;
}

/** 다운로드용: 페이지네이션을 순회하며 해당 contact의 Company Relations 전체를 조회 */
async function fetchAllCompanyRelations(): Promise<CompanyRelation[]> {
    const cid = String(props.contactId || '').trim();
    if (!cid) return [];
    const all: CompanyRelation[] = [];
    const PAGE = 200;
    let offset = 0;
    // 무한루프 방지 상한
    for (let guard = 0; guard < 1000; guard++) {
        const response = await contactsApi.getCompanyRelations(cid, { limit: PAGE, offset });
        const responseData = response as unknown as {
            companyRelations?: CompanyRelation[];
            data?: { companyRelations?: CompanyRelation[]; hasMore?: boolean };
            hasMore?: boolean;
        };
        const chunk = responseData.companyRelations || responseData.data?.companyRelations || [];
        const list = Array.isArray(chunk) ? chunk : [];
        all.push(...list);
        const more =
            typeof responseData.hasMore === 'boolean'
                ? responseData.hasMore
                : typeof responseData.data?.hasMore === 'boolean'
                  ? responseData.data.hasMore
                  : list.length >= PAGE;
        if (!more || list.length === 0) break;
        offset += list.length;
    }
    return all;
}

/** exceljs로 워크북 생성 후 xlsx 파일 다운로드 (Entity_my 목록 화면과 동일 패턴) */
async function buildAndDownloadExcel(rows: CompanyRelation[]) {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Company Relations');

    // 주소를 "유형별 컬럼"(Residential / Service / Business …)으로 전개
    const entriesByRow = rows.map((r) => relationAddressEntries(r));

    // 전체 행에서 등장하는 주소 유형 수집 (표시 우선순위대로 정렬)
    const PRIORITY = ['residential', 'service', 'business', 'registered', 'correspondence', 'address'];
    const seenTypes = new Map<string, string>(); // typeKey -> 대표 라벨(기타 유형 헤더용)
    for (const entries of entriesByRow) {
        for (const e of entries) {
            if (!seenTypes.has(e.typeKey)) seenTypes.set(e.typeKey, e.label);
        }
    }
    const typeKeys = [...seenTypes.keys()].sort((a, b) => {
        const ia = PRIORITY.indexOf(a);
        const ib = PRIORITY.indexOf(b);
        const pa = ia === -1 ? PRIORITY.length : ia;
        const pb = ib === -1 ? PRIORITY.length : ib;
        if (pa !== pb) return pa - pb;
        return a.localeCompare(b);
    });
    if (typeKeys.length === 0) typeKeys.push('address');

    const headerLabels = [t('contact.Company Name') || 'Company Name'];
    for (const tk of typeKeys) headerLabels.push(addressColumnHeader(tk, seenTypes.get(tk) || ''));
    headerLabels.push(t('contact.Role') || 'Role', t('contact.Shares') || 'Shares');

    const headerRow = worksheet.addRow(headerLabels);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1B84FF' } };
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    });

    rows.forEach((item, rowIdx) => {
        const entries = entriesByRow[rowIdx];
        // 같은 유형이 여러 개면 줄바꿈으로 합침
        const byType = new Map<string, string[]>();
        for (const e of entries) {
            const arr = byType.get(e.typeKey) || [];
            arr.push(e.line);
            byType.set(e.typeKey, arr);
        }
        const addrCells = typeKeys.map((tk) => (byType.get(tk) || []).join('\n'));
        const dataRow = worksheet.addRow([
            String(item.companyName || ''),
            ...addrCells,
            relationRoleLabel(item),
            relationSharesText(item)
        ]);
        dataRow.eachCell((cell) => {
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
    });

    const lastAddrIndex = typeKeys.length; // 0=Company, 1..N=Address 유형, 이후 Role/Shares
    worksheet.columns.forEach((column, index) => {
        if (index === 0) column.width = 32;
        else if (index >= 1 && index <= lastAddrIndex) column.width = 55;
        else column.width = 18;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    // 파일명: 컨택트 이름 + " (" + reg_no + ")"  (GUID 대신)
    const rawName = String(props.contactName || '').trim() || String(props.contactId || '').trim();
    const regNo = String(props.contactRegNo || '').trim();
    // Windows에서 허용되지 않는 파일명 문자 제거
    const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
    const baseName = sanitize(regNo ? `${rawName} (${regNo})` : rawName);
    const fileName = `Company_Relations_${baseName}_${dateStr}.xlsx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * SG 전용 엑셀. 컬럼이 MY 와 다르다 — 주소 컬럼이 없고 Registration Number 가 있다.
 *   Company Name │ Registration Number │ Role │ Shares
 * ★ MY 의 buildAndDownloadExcel 은 건드리지 않는다(위 함수 그대로).
 */
async function buildAndDownloadExcelSg(rows: CompanyRelation[]) {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Company Relations');

    const BORDER = {
        top: { style: 'thin' as const },
        left: { style: 'thin' as const },
        bottom: { style: 'thin' as const },
        right: { style: 'thin' as const },
    };

    const headerRow = worksheet.addRow([
        t('contact.Company Name') || 'Company Name',
        t('contact.Registration Number') || 'Registration Number',
        t('contact.Role') || 'Role',
        t('contact.Shares') || 'Shares',
    ]);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1B84FF' } };
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = BORDER;
    });

    rows.forEach((item) => {
        const dataRow = worksheet.addRow([
            String(item.companyName || ''),
            String(item.companyRegNo || ''),
            relationRoleLabel(item),      // Officer 면 item.position → 'Director (Nominee)'
            relationSharesText(item),
        ]);
        dataRow.eachCell((cell) => {
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            cell.border = BORDER;
        });
    });

    worksheet.columns.forEach((column, index) => {
        column.width = index === 0 ? 32 : 22;
    });

    // 아래 저장 로직은 buildAndDownloadExcel 과 동일하다.
    //   공통 헬퍼로 빼면 MY 함수를 수정해야 하므로 의도적으로 복제했다.
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const rawName = String(props.contactName || '').trim() || String(props.contactId || '').trim();
    const regNo = String(props.contactRegNo || '').trim();
    const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
    const baseName = sanitize(regNo ? `${rawName} (${regNo})` : rawName);
    const fileName = `Company_Relations_${baseName}_${dateStr}.xlsx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── SG 직책 선택 팝업 ────────────────────────────────────────────
const roleDialogOpen = ref(false);
const roleOptions = ref<string[]>([]);
const selectedRoles = ref<string[]>([]);
const pendingRows = ref<CompanyRelation[]>([]);

/**
 * SG: 조회를 먼저 하고 팝업을 연다.
 * 건수는 안 띄우지만 "실제로 있는 직책만" 보여주려면 조회가 먼저여야 한다.
 */
async function openSgRoleDialog(cid: string) {
    isExporting.value = true;
    try {
        try {
            await contactsApi.verifyCompanyRelationsAccess(cid);
        } catch (accessErr) {
            const status = isAxiosError(accessErr) ? accessErr.response?.status : undefined;
            if (status === 403 || status === 401) {
                await dialogError(
                    t('contact.Access Denied') || 'Access Denied',
                    t('contact.Company relations download denied') ||
                        'You do not have permission to download the company relations of this contact.'
                );
                return;
            }
            throw accessErr;
        }

        const rows = await fetchAllCompanyRelations();
        const options = buildRoleOptions(rows);
        if (!rows.length || !options.length) {
            await dialogError(
                t('contact.Excel Download') || 'Excel Download',
                t('contact.No company relations to download') ||
                    'There are no company relations to download.'
            );
            return;
        }

        pendingRows.value = rows;
        roleOptions.value = options;
        selectedRoles.value = [...options];   // 기본 전체 선택
        roleDialogOpen.value = true;
    } catch (err) {
        console.error('[CompanyRelationsTable] SG role dialog failed:', err);
        await dialogError(
            t('contact.Error') || 'Error',
            t('contact.Excel export failed') || 'Excel export failed.'
        );
    } finally {
        isExporting.value = false;
    }
}

async function onRoleDialogConfirm() {
    if (!selectedRoles.value.length) return;
    isExporting.value = true;
    try {
        const rows = projectRowsForExcel(pendingRows.value, selectedRoles.value);
        if (!rows.length) {
            await dialogError(
                t('contact.Excel Download') || 'Excel Download',
                t('contact.No company relations to download') ||
                    'There are no company relations to download.'
            );
            return;
        }
        await buildAndDownloadExcelSg(rows);
        roleDialogOpen.value = false;
    } catch (err) {
        console.error('[CompanyRelationsTable] SG excel export failed:', err);
        await dialogError(
            t('contact.Error') || 'Error',
            t('contact.Excel export failed') || 'Excel export failed.'
        );
    } finally {
        isExporting.value = false;
    }
}

async function handleDownloadClick() {
    if (isExporting.value) return;
    const cid = String(props.contactId || '').trim();
    if (!cid) return;

    // SG 는 팝업 자체가 확인 단계라 아래 confirm 을 타지 않는다. 이하 MY 경로는 무변경.
    if (isSgJurisdiction.value) {
        await openSgRoleDialog(cid);
        return;
    }

    const ok = await confirm({
        title: t('contact.Excel Download') || 'Excel Download',
        message:
            t('contact.Download company relations confirm') ||
            'Download all company relations of this contact as an Excel file?',
        type: 'question'
    });
    if (!ok) return;

    isExporting.value = true;
    try {
        // 1) 권한 검증 — 현재 로그인 client가 이 contact의 소유 client가 아니면 403(AUTH002)
        try {
            await contactsApi.verifyCompanyRelationsAccess(cid);
        } catch (accessErr) {
            const status = isAxiosError(accessErr) ? accessErr.response?.status : undefined;
            if (status === 403 || status === 401) {
                await dialogError(
                    t('contact.Access Denied') || 'Access Denied',
                    t('contact.Company relations download denied') ||
                        'You do not have permission to download the company relations of this contact.'
                );
                return;
            }
            throw accessErr;
        }

        // 2) 전체 Company Relations 조회
        const rows = await fetchAllCompanyRelations();
        if (!rows.length) {
            await dialogError(
                t('contact.Excel Download') || 'Excel Download',
                t('contact.No company relations to download') ||
                    'There are no company relations to download.'
            );
            return;
        }

        // 3) 엑셀 생성/다운로드
        await buildAndDownloadExcel(rows);
    } catch (err) {
        console.error('[CompanyRelationsTable] Excel export failed:', err);
        await dialogError(
            t('contact.Error') || 'Error',
            t('contact.Excel export failed') || 'Excel export failed.'
        );
    } finally {
        isExporting.value = false;
    }
}

function disconnectLoadMoreObserver() {
    if (loadMoreObserver) {
        loadMoreObserver.disconnect();
        loadMoreObserver = null;
    }
}

async function setupLoadMoreObserver() {
    await nextTick();
    disconnectLoadMoreObserver();
    const root = scrollRootRef.value;
    const target = loadMoreSentinelRef.value;
    if (!root || !target) return;
    loadMoreObserver = new IntersectionObserver(
        (entries) => {
            const hit = entries.some((e) => e.isIntersecting);
            if (hit) void loadRelationsPage(false);
        },
        { root, rootMargin: '160px', threshold: 0 }
    );
    loadMoreObserver.observe(target);
}

function onSearchDraftUpdate(value: string) {
    searchDraft.value = value;
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
        void loadRelationsPage(true);
    }, 350);
}

async function loadRelationsPage(reset: boolean) {
    const cid = String(props.contactId || '').trim();
    if (!cid) return;

    if (!reset) {
        if (!hasMore.value || loadingMore.value || isLoading.value) return;
    } else {
        companyRelations.value = [];
        hasMore.value = true;
        totalCount.value = null;
    }

    const offset = reset ? 0 : companyRelations.value.length;
    const search = searchDraft.value.trim();

    const showFullScreenLoading = reset || companyRelations.value.length === 0;
    if (showFullScreenLoading) isLoading.value = true;
    else loadingMore.value = true;

    try {
        const response = await contactsApi.getCompanyRelations(cid, {
            limit: PAGE_SIZE,
            offset,
            search: search || undefined
        });
        // 백엔드 sendSuccessResponse는 payload를 최상위에 펼침(ApiResponse.data와 다름)
        const responseData = response as unknown as {
            companyRelations?: CompanyRelation[];
            data?: { companyRelations?: CompanyRelation[]; hasMore?: boolean; totalCount?: number };
            hasMore?: boolean;
            totalCount?: number;
        };
        const chunk =
            responseData.companyRelations ||
            responseData.data?.companyRelations ||
            [];
        const list = Array.isArray(chunk) ? chunk : [];

        if (typeof responseData.hasMore === 'boolean') {
            hasMore.value = responseData.hasMore;
        } else if (typeof responseData.data?.hasMore === 'boolean') {
            hasMore.value = responseData.data.hasMore;
        } else {
            hasMore.value = list.length >= PAGE_SIZE;
        }

        if (typeof responseData.totalCount === 'number') {
            totalCount.value = responseData.totalCount;
        } else if (typeof responseData.data?.totalCount === 'number') {
            totalCount.value = responseData.data.totalCount;
        }

        if (reset) companyRelations.value = list;
        else companyRelations.value = [...companyRelations.value, ...list];
    } catch (error) {
        console.error('[CompanyRelationsTable] Failed to load company relations:', error);
        if (isAxiosError(error)) {
            console.error('[CompanyRelationsTable] Error details:', error.response?.data);
        }
    } finally {
        isLoading.value = false;
        loadingMore.value = false;
        await setupLoadMoreObserver();
    }
}

watch(
    () => props.contactId,
    async (id) => {
        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer);
            searchDebounceTimer = null;
        }
        searchDraft.value = '';
        const cid = String(id || '').trim();
        if (!cid) {
            companyRelations.value = [];
            hasMore.value = false;
            totalCount.value = null;
            disconnectLoadMoreObserver();
            return;
        }
        await loadRelationsPage(true);
    },
    { immediate: true }
);

onUnmounted(() => {
    disconnectLoadMoreObserver();
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
});

watch(
    () => [props.contactJsonData, props.profileJsonData, props.residentialAddress],
    async () => {
        const raw = props.contactJsonData;
        const prof = props.profileJsonData;
        const res = props.residentialAddress;
        let parsed: Record<string, any> | null = null;
        if (typeof raw === 'string') {
            try {
                parsed = JSON.parse(raw);
            } catch {
                parsed = null;
            }
        } else if (raw && typeof raw === 'object') {
            parsed = raw as Record<string, any>;
        }
        let profParsed: Record<string, any> | null = null;
        if (typeof prof === 'string') {
            try {
                profParsed = JSON.parse(prof);
            } catch {
                profParsed = null;
            }
        } else if (prof && typeof prof === 'object') {
            profParsed = prof as Record<string, any>;
        }
        const need =
            (typeof raw === 'string' && /"country"\s*:\s*"[A-Z]\d{5,}"/.test(raw)) ||
            (typeof prof === 'string' && /"country"\s*:\s*"[A-Z]\d{5,}"/.test(prof)) ||
            jsonAnyRowHasCountryCode(parsed) ||
            jsonAnyRowHasCountryCode(profParsed) ||
            (Array.isArray(res) &&
                res.some((addr: any) => isLikelyCodeValue(String(addr?.country || '').trim()))) ||
            (res &&
                typeof res === 'object' &&
                !Array.isArray(res) &&
                isLikelyCodeValue(String((res as any)?.country || '').trim()));
        if (need) await loadCountryCodeNameMap();
    },
    { deep: true }
);

function jsonAnyRowHasCountryCode(obj: Record<string, any> | null): boolean {
    if (!obj || typeof obj !== 'object') return false;
    for (const key of ['service_address', 'business_address', 'residential_address']) {
        const block = obj[key];
        const rows = Array.isArray(block) ? block : block && typeof block === 'object' ? [block] : [];
        if (rows.some((addr: any) => isLikelyCodeValue(String(addr?.country || '').trim()))) return true;
    }
    return false;
}

onMounted(async () => {
    // 주소 country가 코드값(B15135 등)일 수 있어 코드명 맵을 미리 로드
    const raw = props.contactJsonData;
    const prof = props.profileJsonData;
    const res = props.residentialAddress;
    let parsedForCountry: Record<string, any> | null = null;
    if (typeof raw === 'string') {
        try {
            parsedForCountry = JSON.parse(raw);
        } catch {
            parsedForCountry = null;
        }
    } else if (raw && typeof raw === 'object') {
        parsedForCountry = raw as Record<string, any>;
    }
    let profParsed: Record<string, any> | null = null;
    if (typeof prof === 'string') {
        try {
            profParsed = JSON.parse(prof);
        } catch {
            profParsed = null;
        }
    } else if (prof && typeof prof === 'object') {
        profParsed = prof as Record<string, any>;
    }

    const hasCountryCodeInJson =
        (typeof raw === 'string' && /"country"\s*:\s*"[A-Z]\d{5,}"/.test(raw)) ||
        (typeof prof === 'string' && /"country"\s*:\s*"[A-Z]\d{5,}"/.test(prof)) ||
        jsonAnyRowHasCountryCode(parsedForCountry) ||
        jsonAnyRowHasCountryCode(profParsed);
    const resCountry =
        Array.isArray(res) &&
        res.some((addr: any) => isLikelyCodeValue(String(addr?.country || '').trim()));
    const resObjCountry =
        res &&
        typeof res === 'object' &&
        !Array.isArray(res) &&
        isLikelyCodeValue(String((res as any)?.country || '').trim());
    if (hasCountryCodeInJson || resCountry || resObjCountry) {
        await loadCountryCodeNameMap();
    }
});

</script>

<template>
    <v-card rounded="md" class="contact-company-relations-section">
        <v-card-title class="contact-section-title d-flex align-center">
            <span>{{ sectionTitle }}</span>
            <v-spacer />
            <v-btn
                v-if="isMyJurisdiction || isSgJurisdiction"
                color="primary"
                variant="tonal"
                size="small"
                class="text-none company-relations-download-btn"
                :loading="isExporting"
                :disabled="isExporting || !props.contactId"
                @click="handleDownloadClick"
            >
                <v-icon start size="18">mdi-microsoft-excel</v-icon>
                {{ t('contact.Excel Download') || 'Excel Download' }}
            </v-btn>
        </v-card-title>
        <v-card-text class="company-relations-wrapper">
            <v-text-field
                :model-value="searchDraft"
                density="compact"
                variant="outlined"
                hide-details="auto"
                clearable
                prepend-inner-icon="mdi-magnify"
                :placeholder="t('contact.Search company name') || 'Search by company name'"
                class="company-relations-search px-4 pt-4 pb-2"
                @update:model-value="onSearchDraftUpdate"
            />
            <div ref="scrollRootRef" class="company-relations-scroll">
                <v-data-table
                    :headers="headers"
                    :items="tableRelationItems"
                    :loading="isLoading"
                    :items-per-page="-1"
                    hide-default-footer
                    :class="[
                        'elevation-0',
                        'company-relations-table',
                        showAddressColumn ? 'company-relations-table--wide-address' : ''
                    ]"
                >
                <template #item.address="{ item }">
                    <div class="d-flex align-center flex-nowrap gap-2 address-cell">
                        <span class="address-cell__line">{{ relationPrimaryAddressLine(item) || '—' }}</span>
                        <v-chip
                            v-if="relationMoreAddressCount(item) > 0"
                            size="small"
                            color="primary"
                            variant="tonal"
                            class="address-more-chip"
                            @click="openAddressDetailModal(item)"
                        >
                            {{ t('contact.More') }}+{{ relationMoreAddressCount(item) }}
                        </v-chip>
                    </div>
                </template>
                <!-- Role 컬럼: Officer 인 경우 POSITION_NAME(= position) 표시 -->
                <template #item.role="{ item }">
                    <span>
                        {{
                            item?.role === 'Officer' && item?.position
                                ? item.position
                                : item?.role
                        }}
                    </span>
                </template>
                <template #item.shares="{ item }">
                    <span>
                        {{
                            item?.role === 'Shares' && item?.shares !== undefined && item?.shares !== null
                                ? `${item.currency ? item.currency + ' ' : ''}${Number(item.shares).toLocaleString()}`
                                : '-'
                        }}
                    </span>
                </template>
            </v-data-table>

                <div
                    ref="loadMoreSentinelRef"
                    class="company-relations-sentinel d-flex justify-center align-center py-3"
                >
                    <v-progress-circular
                        v-if="loadingMore && hasMore"
                        indeterminate
                        size="22"
                        width="2"
                        color="primary"
                    />
                </div>
            </div>

            <v-dialog
                v-model="addressDetailModalOpen"
                max-width="560"
                scrollable
                class="address-detail-dialog-root"
            >
                <v-card class="address-detail-card" rounded="lg" elevation="8">
                    <v-card-title class="address-detail-card__title d-flex align-center">
                        <v-icon class="mr-2" color="primary" size="22">mdi-map-marker-radius</v-icon>
                        <span>{{ t('contact.Address detail') }}</span>
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="address-detail-card__body pa-5">
                        <template v-if="modalUsesMyRoleAddresses">
                            <section
                                v-for="(row, idx) in modalMyRoleAddresses"
                                :key="'my-addr-' + idx"
                                class="address-detail-section"
                            >
                                <div class="address-detail-section__label">
                                    <template v-if="row.positionName">{{ row.positionName }}</template>
                                    <template v-else>{{ addressModalRelation?.role }}</template>
                                    <span v-if="row.addressLabel" class="address-detail-meta">· {{ row.addressLabel }}</span>
                                </div>
                                <div class="address-detail-section__value text-wrap">
                                    {{ upperAddressLine(row.addressLine) || '—' }}
                                </div>
                            </section>
                        </template>

                        <template v-else>
                        <section class="address-detail-section">
                            <div class="address-detail-section__label">
                                {{ t('contact.Residential Address') }}
                            </div>
                            <div class="address-detail-section__value text-wrap">
                                {{ defaultResidentialLine || '—' }}
                            </div>
                        </section>

                        <template v-if="modalFilteredService.length">
                            <section
                                v-for="(row, idx) in modalFilteredService"
                                :key="'svc-' + idx"
                                class="address-detail-section"
                            >
                                <div class="address-detail-section__label">
                                    {{ t('contact.Service Address') }}
                                    <template v-if="positionLabel(row)">
                                        <span class="address-detail-meta">· {{ positionLabel(row) }}</span>
                                    </template>
                                    <template v-if="entityDisplayName(row)">
                                        <span class="address-detail-meta">· {{ entityDisplayName(row) }}</span>
                                    </template>
                                </div>
                                <div class="address-detail-section__value text-wrap">
                                    {{ normalizeAddressText(row) || '—' }}
                                </div>
                            </section>
                        </template>

                        <template v-if="modalFilteredBusiness.length">
                            <section
                                v-for="(row, idx) in modalFilteredBusiness"
                                :key="'biz-' + idx"
                                class="address-detail-section"
                            >
                                <div class="address-detail-section__label">
                                    {{ t('contact.Business Address') }}
                                    <template v-if="positionLabel(row)">
                                        <span class="address-detail-meta">· {{ positionLabel(row) }}</span>
                                    </template>
                                    <template v-if="entityDisplayName(row)">
                                        <span class="address-detail-meta">· {{ entityDisplayName(row) }}</span>
                                    </template>
                                </div>
                                <div class="address-detail-section__value text-wrap">
                                    {{ normalizeAddressText(row) || '—' }}
                                </div>
                            </section>
                        </template>
                        </template>
                    </v-card-text>
                    <v-divider />
                    <v-card-actions class="pa-3">
                        <v-spacer />
                        <v-btn color="primary" variant="flat" rounded="lg" @click="addressDetailModalOpen = false">
                            {{ t('contact.Close') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- SG 전용: 직책 선택 후 엑셀 다운로드. MY 는 이 팝업을 타지 않는다. -->
            <v-dialog v-model="roleDialogOpen" max-width="380" persistent>
                <v-card rounded="lg">
                    <v-card-title class="text-subtitle-1">
                        {{ t('contact.Select roles') || 'Select roles' }}
                    </v-card-title>
                    <v-card-subtitle class="pb-2">
                        {{ t('contact.Select roles hint') || 'Only the selected roles will be exported.' }}
                    </v-card-subtitle>
                    <v-divider />
                    <v-card-text class="py-2">
                        <v-checkbox
                            v-for="opt in roleOptions"
                            :key="opt"
                            v-model="selectedRoles"
                            :value="opt"
                            :label="opt"
                            density="compact"
                            hide-details
                        />
                    </v-card-text>
                    <v-divider />
                    <v-card-actions class="pa-3">
                        <v-btn variant="text" size="small" @click="selectedRoles = [...roleOptions]">
                            {{ t('contact.Select all') || 'Select all' }}
                        </v-btn>
                        <v-spacer />
                        <v-btn variant="text" :disabled="isExporting" @click="roleDialogOpen = false">
                            {{ t('common.Cancel') || 'Cancel' }}
                        </v-btn>
                        <v-btn
                            color="primary"
                            variant="flat"
                            rounded="lg"
                            :disabled="!selectedRoles.length"
                            :loading="isExporting"
                            @click="onRoleDialogConfirm"
                        >
                            {{ t('contact.Excel Download') || 'Excel Download' }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card-text>
    </v-card>
</template>

<style scoped lang="scss">
.contact-company-relations-section {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

/* 폰트 패밀리 */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contact-section-title {
    font-size: 18px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.company-relations-wrapper {
    padding: 0;
}

.company-relations-scroll {
    max-height: 480px;
    overflow-y: auto;
    overflow-x: auto;
}

.company-relations-search {
    max-width: 400px;
}

/* 테이블 헤더 - Contents B (12px) */
.company-relations-table :deep(thead th) {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: rgb(var(--v-theme-on-surface)) !important;
    padding: 16px;
}

/* 라이트 모드에서만 회색 배경 적용 */
:deep(.v-theme:not([class*='DARK_'])) .company-relations-table :deep(thead th) {
    background-color: #F5F5F5 !important;
    color: #3C4650 !important;
}

/* 테이블 바디 - Content A (14px) */
.company-relations-table :deep(tbody td) {
    font-size: 14px;
    color: rgb(var(--v-theme-on-surface));
    padding: 16px;
}

/* 라이트 모드 테이블 바디 */
:deep(.v-theme:not([class*='DARK_'])) .company-relations-table :deep(tbody td) {
    color: #3C4650;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

/* 테이블 호버 효과 */
.company-relations-table :deep(tbody tr:hover) {
    background-color: rgba(27, 132, 255, 0.04) !important;
}

/* 다크모드 테이블 헤더 스타일 */
:deep(.v-theme--dark) .company-relations-table :deep(thead th),
:deep([class*='v-theme--DARK_']) .company-relations-table :deep(thead th) {
    background-color: rgb(var(--v-theme-surface)) !important;
    color: #FFFFFF !important;
}

/* 다크모드 테이블 바디 스타일 */
:deep(.v-theme--dark) .company-relations-table :deep(tbody td),
:deep([class*='v-theme--DARK_']) .company-relations-table :deep(tbody td) {
    color: #FFFFFF !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
}

/* 다크모드 호버 효과 */
:deep(.v-theme--dark) .company-relations-table :deep(tbody tr:hover),
:deep([class*='v-theme--DARK_']) .company-relations-table :deep(tbody tr:hover) {
    background-color: rgba(255, 255, 255, 0.08) !important;
}

.company-relations-table :deep(.v-table__wrapper) {
    max-height: none;
    overflow: visible;
}

.text-wrap {
    white-space: normal;
    word-break: break-word;
}

/* Address 컬럼: 한 줄 + 넓은 영역 (헤더 minWidth와 함께) */
.company-relations-table--wide-address :deep(th:nth-child(2)),
.company-relations-table--wide-address :deep(td:nth-child(2)) {
    min-width: 560px;
    width: 50%;
    max-width: none;
    white-space: nowrap;
    vertical-align: middle;
}

.address-cell {
    width: 100%;
    min-width: 0;
    white-space: nowrap;
}

.address-cell__line {
    line-height: 1.45;
    white-space: nowrap;
    flex: 0 1 auto;
}

.address-more-chip {
    cursor: pointer;
    font-weight: 600;
    flex-shrink: 0;
}

.address-detail-card__title {
    font-size: 1.05rem;
    font-weight: 600;
    padding: 16px 20px;
}

.address-detail-card__body {
    background: rgba(var(--v-theme-surface-variant), 0.25);
}

.address-detail-section {
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.address-detail-section:last-child {
    margin-bottom: 0;
}

.address-detail-section__label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(var(--v-theme-on-surface), 0.65);
    margin-bottom: 8px;
}

.address-detail-meta {
    font-weight: 500;
    text-transform: none;
    letter-spacing: normal;
    color: rgb(var(--v-theme-primary));
}

.address-detail-section__value {
    font-size: 0.9375rem;
    line-height: 1.5;
    color: rgb(var(--v-theme-on-surface));
}
</style>

